import * as React from "react";
import { useMemo } from "react";
import { TrainingStateGraph } from "./TrainingStateGraph.component";
const HORIZONTAL_SPACING = 170;
const VERTICAL_SPACING = 150;
export const TrainingStageTab = ({ scenario, currentStageId, debugVersion }) => {
    const graph = useMemo(() => buildStageGraph(scenario), [scenario, debugVersion]);
    const graphKey = useMemo(() => graph.nodes.map((node) => node.id).sort().join("|"), [graph.nodes]);
    if (scenario === undefined) {
        return (React.createElement("div", { className: "training-stage-empty" }, "No training scenario available yet."));
    }
    return (React.createElement(TrainingStateGraph, { graphKey: graphKey, nodes: graph.nodes, edges: graph.edges, defaultPositions: graph.defaultPositions, currentNodeId: currentStageId, nodeRadius: 42, hintText: "Left drag: move stage or label. Right drag: pan.", arrowMarkerId: "training-stage-arrow" }));
};
function buildStageGraph(scenario) {
    var _a;
    if (scenario === undefined) {
        return {
            nodes: [],
            edges: [],
            defaultPositions: {}
        };
    }
    const stateNodes = scenario.getAllStates();
    const sequenceIndexByStageId = buildSequenceIndexMap((_a = scenario.sequences) !== null && _a !== void 0 ? _a : []);
    const eventConditionInfoById = buildEventConditionInfoMap(scenario.events);
    const edges = [];
    const adjacency = new Map();
    const transitionsByPair = new Map();
    stateNodes.forEach((stateNode) => {
        const transitions = stateNode.getTransitions();
        const nextStateIds = Array.from(new Set(transitions.map((transition) => transition.nextState.data.id)));
        adjacency.set(stateNode.data.id, nextStateIds);
        transitions.forEach((transition) => {
            var _a;
            const nextStageId = transition.nextState.data.id;
            const transitionCondition = transition.condition;
            const eventNames = Array.isArray(transitionCondition.eventNames)
                ? transitionCondition.eventNames
                : [transitionCondition.eventNames];
            const labelSegments = buildStageEdgeLabelSegments(eventNames, eventConditionInfoById);
            const pairKey = `${stateNode.data.id}->${nextStageId}`;
            const pairTransitions = (_a = transitionsByPair.get(pairKey)) !== null && _a !== void 0 ? _a : [];
            pairTransitions.push({
                fromId: stateNode.data.id,
                toId: nextStageId,
                labelSegments
            });
            transitionsByPair.set(pairKey, pairTransitions);
        });
    });
    transitionsByPair.forEach((pairTransitions, pairKey) => {
        pairTransitions.forEach((transition, index) => {
            edges.push({
                id: `${pairKey}-${index}`,
                fromId: transition.fromId,
                toId: transition.toId,
                labelSegments: transition.labelSegments,
                groupIndex: index,
                groupCount: pairTransitions.length,
                hasReverseEdge: transitionsByPair.has(`${transition.toId}->${transition.fromId}`)
            });
        });
    });
    const levels = computeLevels(stateNodes, adjacency);
    const nodes = stateNodes.map((stateNode) => {
        var _a, _b;
        return ({
            id: stateNode.data.id,
            label: stateNode.data.id,
            fill: getNodeFill(stateNode.data.isError, sequenceIndexByStageId.get(stateNode.data.id), (_b = (_a = scenario.sequences) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0)
        });
    });
    const nodesByLevel = new Map();
    nodes.forEach((node) => {
        var _a, _b;
        const level = (_a = levels.get(node.id)) !== null && _a !== void 0 ? _a : 0;
        const levelNodes = (_b = nodesByLevel.get(level)) !== null && _b !== void 0 ? _b : [];
        levelNodes.push(node);
        nodesByLevel.set(level, levelNodes);
    });
    const defaultPositions = {};
    Array
        .from(nodesByLevel.entries())
        .sort((a, b) => a[0] - b[0])
        .forEach(([level, levelNodes]) => {
        levelNodes
            .sort((a, b) => a.id.localeCompare(b.id))
            .forEach((node, index) => {
            defaultPositions[node.id] = {
                x: 120 + index * HORIZONTAL_SPACING,
                y: 100 + level * VERTICAL_SPACING
            };
        });
    });
    return { nodes, edges, defaultPositions };
}
function buildEventConditionInfoMap(events) {
    const conditionMap = new Map();
    for (const conditionalEvent of events) {
        const values = Array.isArray(conditionalEvent.value) ? conditionalEvent.value : [conditionalEvent.value];
        const evaluation = conditionalEvent.condition.evaluateWithTrace(values[0].context);
        for (const event of values) {
            conditionMap.set(event.id, {
                text: conditionalEvent.condition.conditionText,
                traces: evaluation.traces
            });
        }
    }
    return conditionMap;
}
function buildStageEdgeLabelSegments(eventNames, eventConditionInfoById) {
    const segments = [];
    eventNames.forEach((eventName, index) => {
        if (index > 0)
            segments.push({ text: " | " });
        const conditionInfo = eventConditionInfoById.get(eventName);
        if (conditionInfo === undefined) {
            segments.push({ text: eventName });
            return;
        }
        segments.push(...buildConditionLabelSegments(conditionInfo.text, conditionInfo.traces));
    });
    return segments.length > 0 ? segments : [{ text: "-" }];
}
function buildSequenceIndexMap(sequences) {
    const result = new Map();
    sequences.forEach((sequence, index) => {
        sequence.stages.forEach((stage) => {
            if (!result.has(stage.id)) {
                result.set(stage.id, index);
            }
        });
    });
    return result;
}
function computeLevels(stateNodes, adjacency) {
    var _a, _b, _c;
    const levels = new Map();
    const root = (_a = stateNodes[0]) === null || _a === void 0 ? void 0 : _a.data.id;
    if (root !== undefined) {
        const queue = [root];
        levels.set(root, 0);
        while (queue.length > 0) {
            const currentId = queue.shift();
            if (currentId === undefined)
                continue;
            const currentLevel = (_b = levels.get(currentId)) !== null && _b !== void 0 ? _b : 0;
            for (const nextId of (_c = adjacency.get(currentId)) !== null && _c !== void 0 ? _c : []) {
                if (levels.has(nextId))
                    continue;
                levels.set(nextId, currentLevel + 1);
                queue.push(nextId);
            }
        }
    }
    let fallbackLevel = (Math.max(0, ...levels.values()) || 0) + 1;
    stateNodes.forEach((stateNode) => {
        if (levels.has(stateNode.data.id))
            return;
        levels.set(stateNode.data.id, fallbackLevel++);
    });
    return levels;
}
function getNodeFill(isError, sequenceIndex, sequenceCount) {
    if (isError)
        return "#d65252";
    if (sequenceIndex === undefined || sequenceCount <= 0)
        return "#b6b6b6";
    const hue = Math.round((360 / sequenceCount) * sequenceIndex) + 15;
    return `hsl(${hue}deg 65% 62%)`;
}
function buildConditionLabelSegments(label, traces) {
    const leafTraces = traces
        .filter((trace) => trace.kind === "leaf" || trace.kind === "literal")
        .sort((a, b) => a.start - b.start);
    if (leafTraces.length === 0) {
        return [{ text: label }];
    }
    const segments = [];
    let currentIndex = 0;
    for (const trace of leafTraces) {
        const start = clamp(trace.start, 0, label.length);
        const end = clamp(trace.end, 0, label.length);
        if (end <= start)
            continue;
        if (start > currentIndex) {
            segments.push({ text: label.slice(currentIndex, start) });
        }
        segments.push({
            text: label.slice(start, end),
            color: trace.result ? "#1a8f3e" : "#c43b3b"
        });
        currentIndex = end;
    }
    if (currentIndex < label.length) {
        segments.push({ text: label.slice(currentIndex) });
    }
    return segments.filter((segment) => segment.text.length > 0);
}
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
